/**
 * LeadsTable component - displays marketing leads with selection capability
 */

import { useState } from 'react';
import type { MarketingLead } from '../../services/marketingApi';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';

interface LeadsTableProps {
  leads: MarketingLead[];
  selectedLeads: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  isLoading?: boolean;
}

export function LeadsTable({
  leads,
  selectedLeads,
  onSelectionChange,
  isLoading,
}: LeadsTableProps) {
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allKeys = new Set(leads.map((lead) => `${lead.customer.id}:${lead.vehicle.id}`));
      onSelectionChange(allKeys);
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectOne = (key: string, checked: boolean) => {
    const newSelection = new Set(selectedLeads);
    if (checked) {
      newSelection.add(key);
    } else {
      newSelection.delete(key);
    }
    onSelectionChange(newSelection);
    setSelectAll(newSelection.size === leads.length);
  };

  const getDaysAgoBadgeVariant = (days: number): 'default' | 'secondary' | 'destructive' => {
    if (days > 365) return 'destructive';
    if (days > 180) return 'secondary';
    return 'default';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No se encontraron leads con los filtros seleccionados.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={selectAll}
                onCheckedChange={handleSelectAll}
                aria-label="Seleccionar todos"
              />
            </TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Vehículo</TableHead>
            <TableHead>Último servicio</TableHead>
            <TableHead className="text-right">Días desde servicio</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((lead) => {
            const key = `${lead.customer.id}:${lead.vehicle.id}`;
            const isSelected = selectedLeads.has(key);

            return (
              <TableRow key={key} className={isSelected ? 'bg-muted/50' : undefined}>
                <TableCell>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => handleSelectOne(key, checked as boolean)}
                    aria-label={`Seleccionar ${lead.customer.name}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="font-medium">{lead.customer.name}</div>
                  <div className="text-sm text-muted-foreground">{lead.customer.email}</div>
                  {lead.customer.phone && (
                    <div className="text-sm text-muted-foreground">{lead.customer.phone}</div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium">
                    {lead.vehicle.make} {lead.vehicle.model}
                    {lead.vehicle.year && ` (${lead.vehicle.year})`}
                  </div>
                  <div className="text-sm text-muted-foreground">{lead.vehicle.registration}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{lead.last_service.service_type}</div>
                  {lead.last_service.performed_at && (
                    <div className="text-xs text-muted-foreground">
                      {new Date(lead.last_service.performed_at * 1000).toLocaleDateString('es-ES')}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Badge variant={getDaysAgoBadgeVariant(lead.last_service.days_ago)}>
                    {lead.last_service.days_ago} días
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
